import { useEffect, useRef, useState } from 'react'
import {
  Button,
  Col,
  ExplicitSelectionButton,
  feedbackcard,
  Grid,
  Input,
  Row,
  StandardUniselection,
  YesObject
} from '@yes.technology/react-toolkit'
import Editor, { OnMount } from '@monaco-editor/react'
import styled from 'styled-components'

import useSitemodelFromUrl from 'site/shared/hooks/useSitemodelFromUrl'
import TopLine from './Renderer/components/TopLine'
import { TopLinePropsType } from './Renderer/components/TopLine/TopLine.types'
import { AlignEnum, RepresentationEnum } from './shared/enums'
import { SelectionMenuOption } from './Renderer/components/FilterFields/components/Field/StandardMultiselectionField'
import apiAsync from 'infrastructure/shared/api/api-async'
import tools from 'shared/assets/img/tools.svg'
import { Renderer, RendererProps } from './Renderer'

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  width: 100%;
`

const StyledSideMenu = styled.div`
  width: 330px;
  border-right: 1px solid #abbdd1;
  background-color: #f7f8fa;
  padding: 2rem;
  box-sizing: border-box;

  position: sticky;
  top: 0;
  height: 100vh;

  h2 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: #303032;
    margin-bottom: 1.5rem;
  }
`

const SitemodelView = styled.div`
  flex: 1;
  padding: 1rem 2rem 0 1rem;
  overflow-y: auto;
  box-sizing: border-box;
`

const StyledJSONEditor = styled.div`
  border: 1px solid #dcdcdc;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-top: 1rem;
  margin-bottom: 1rem;
  overflow: auto;
`

const StyledView = styled.div`
  border: 1px solid #dcdcdc;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-top: 1rem;
  margin-bottom: 1rem;
  overflow: auto;
`

const AreaLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #004099;
  padding: 0.5rem 2rem;
  height: 3rem;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  h1 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #fff;
  }
`

const SaveButton = styled(Button)`
  width: 150px;
  background-color: transparent;
  border-color: #fff;
`

export const SideMenu = ({ sitemodel }: { sitemodel: YesObject }) => {
  return (
    <StyledSideMenu>
      <h2>Dados Sitemodel</h2>
      <Input
        label='UUID do Sitemodel'
        value={sitemodel?.uuid || ''}
        heightClass='regular'
        informational
      />
      <Input
        label='Des'
        value={sitemodel?.des || ''}
        heightClass='regular'
        informational
      />
    </StyledSideMenu>
  )
}

export const View = (props: RendererProps) => {
  return (
    <StyledView>
      <Renderer {...props} />
    </StyledView>
  )
}

type JsonEditorProps = {
  initialJson?: object
  onChange?: (json: object) => void
}

export const JsonEditor = ({ initialJson, onChange }: JsonEditorProps) => {
  const editorRef = useRef<any>(null)
  const [message, setMessage] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)

  const handleEditorDidMount: OnMount = (editor) => {
    editorRef.current = editor
  }

  const handleOnChange = () => {
    const value = editorRef.current?.getValue()
    try {
      const parsed = JSON.parse(value)
      onChange?.(parsed)
      setMessage(null)
    } catch (e: any) {
      setMessage({ type: 'error', text: 'JSON inválido: ' + e.message })
    }
  }

  const [theme, setTheme] = useState<SelectionMenuOption>(() => {
    const saved = localStorage.getItem('editorTheme')
    try {
      return saved ? JSON.parse(saved) : { id: 'light', value: 'light' }
    } catch (e) {
      return { id: 'light', value: 'light' }
    }
  })

  const [fontSize, setFontSize] = useState<SelectionMenuOption>(() => {
    const saved = localStorage.getItem('editorFontSize')
    try {
      return saved ? JSON.parse(saved) : { id: '14', value: '14' }
    } catch (e) {
      return { id: '14', value: '14' }
    }
  })

  useEffect(() => {
    localStorage.setItem('editorTheme', JSON.stringify(theme))
  }, [theme])

  useEffect(() => {
    localStorage.setItem('editorFontSize', JSON.stringify(fontSize))
  }, [fontSize])

  return (
    <StyledJSONEditor>
      <Grid style={{ padding: '1rem' }}>
        <Row>
          <Col lg={3}>
            <StandardUniselection
              heightClass='semiSmall'
              selectedOption={theme}
              options={[
                { id: 'light', value: 'light' },
                { id: 'vs-dark', value: 'vs-dark' }
              ]}
              onSelect={(option) => {
                if (option && typeof option === 'object' && 'id' in option) {
                  setTheme(option)
                }
              }}
              label='Tema'
            />
          </Col>
          <Col lg={3}>
            <StandardUniselection
              heightClass='semiSmall'
              selectedOption={fontSize}
              options={[
                { id: '12', value: '12' },
                { id: '14', value: '14' },
                { id: '16', value: '16' },
                { id: '18', value: '18' },
                { id: '20', value: '20' }
              ]}
              onSelect={(option) => {
                if (option && typeof option === 'object' && 'id' in option) {
                  setFontSize(option)
                }
              }}
              label='Tamanho da Fonte'
            />
          </Col>
          <Col lg={6}>
            {message && (
              <div
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem',
                  backgroundColor:
                    message.type === 'success' ? '#c8f7c5' : '#f8d7da',
                  color: message.type === 'success' ? '#2e7d32' : '#a94442',
                  border: '1px solid',
                  borderColor:
                    message.type === 'success' ? '#a5d6a7' : '#f5c6cb',
                  borderRadius: '4px'
                }}
              >
                {message.text}
              </div>
            )}
          </Col>
        </Row>
      </Grid>
      <Editor
        height='90vh'
        defaultLanguage='json'
        defaultValue={JSON.stringify(initialJson, null, 4)}
        onMount={handleEditorDidMount}
        onChange={handleOnChange}
        theme={theme.id}
        options={{
          minimap: { enabled: true },
          wordWrap: 'off',
          formatOnPaste: true,
          formatOnType: true,
          scrollBeyondLastLine: false,
          fontSize: fontSize.id,
          tabSize: 4,
          automaticLayout: true,
          folding: true,
          foldingHighlight: true,
          foldingStrategy: 'auto'
        }}
      />
    </StyledJSONEditor>
  )
}

const Edit = () => {
  const { sitemodel, refetchSitemodelFromUrl } = useSitemodelFromUrl()

  const sitemodelContent = sitemodel?.sitemodel_content

  const [viewType, setViewType] = useState<string>('view')

  const [objectState, setObjectState] = useState<object>({})

  useEffect(() => {
    if (sitemodelContent && typeof sitemodelContent === 'string') {
      setObjectState(JSON.parse(sitemodelContent))
    }
  }, [sitemodelContent])

  const propsTopLine: TopLinePropsType = {
    layout: {
      position_1: [
        {
          order: 1,
          align: AlignEnum.Right,
          representation: RepresentationEnum.LogoArea,
          image: {
            desktop: {
              alt: 'Logotipo para desktop',
              src: 'https://d2qqjx0lx9cu56.cloudfront.net/logos/logo-yes.svg'
            }
          },
          des: 'Yes Network',
          link: '/'
        }
      ],
      position_3: [
        {
          order: 1,
          align: AlignEnum.Left,
          representation: RepresentationEnum.ManagementDashboard,
          components: [
            {
              uuid: 'f91fae3d-c3b4-4d09-b6b9-476ebf6a4b10',
              des: 'Login'
            }
          ]
        }
      ]
    }
  }

  const handleSave = async () => {
    const response = await apiAsync.requestSync({
      endpoint: 'msPlatform',
      path: `objects/${sitemodel?.uuid}`,
      method: 'PATCH',
      obj: {
        uuid_Objectclass: window.idObjectclassSitemodel,
        sitemodel_content: JSON.stringify(objectState),
        version: sitemodel?.version
      },
      showLoading: true
    })

    if (response.success) {
      feedbackcard('Sucesso!', {
        message: 'Sitemodel modificado com sucesso.',
        type: 'success'
      })
      refetchSitemodelFromUrl()
    } else {
      feedbackcard('Erro!', {
        message: response.message || 'Erro ao modificar o sitemodel.',
        type: 'error'
      })
    }
  }

  return (
    <div>
      <TopLine props={propsTopLine} />
      <AreaLine>
        <Title>
          <img src={tools} alt='Tools' />
          <h1>Criação Sitemodel</h1>
        </Title>
        <SaveButton onClick={handleSave} heightClass='semiSmall'>
          Salvar
        </SaveButton>
      </AreaLine>
      <Container>
        {sitemodel && <SideMenu sitemodel={sitemodel} />}

        <SitemodelView>
          <ExplicitSelectionButton
            direction='horizontal'
            heightClass='small'
            onChange={(option) => setViewType(option.uuid)}
            checkedOption={viewType}
            optionLabelsPosition='left'
            options={[
              {
                uuid: 'view',
                des: 'View'
              },
              {
                uuid: 'json-editor',
                des: 'JSON Editor'
              }
            ]}
          />

          {viewType === 'view' && <View {...objectState} />}

          {viewType === 'json-editor' && (
            <JsonEditor initialJson={objectState} onChange={setObjectState} />
          )}
        </SitemodelView>
      </Container>
    </div>
  )
}

export default Edit
